import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Input, Spin, Tag } from "@arco-design/web-react";
import { MapPin, Search } from "lucide-react";

import type { HotelMapSearchItem } from "../types";

const SEARCH_LABEL = "搜索房资源酒店名称或地址";

type HotelMapSearchBarProps = {
  areas: HotelMapSearchItem[];
  points: HotelMapSearchItem[];
  query: string;
  searching: boolean;
  onQueryChange: (value: string) => void;
  onSelect: (item: HotelMapSearchItem) => void;
};

/** 渲染支持鼠标、键盘与辅助技术访问的地点搜索框。 */
export function HotelMapSearchBar({
  areas,
  points,
  query,
  searching,
  onQueryChange,
  onSelect,
}: HotelMapSearchBarProps) {
  const baseId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const items = useMemo(() => [...areas, ...points], [areas, points]);
  const listboxId = `${baseId}-hotel-map-search-results`;
  const activeOptionId = activeIndex >= 0 ? optionId(baseId, activeIndex) : undefined;

  useEffect(() => {
    setActiveIndex((current) => {
      if (current < 0) {
        return current;
      }
      return items.length === 0 ? -1 : Math.min(current, items.length - 1);
    });
  }, [items.length]);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const selectItem = (item: HotelMapSearchItem) => {
    onSelect(item);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      if (items.length === 0) return;
      setActiveIndex((current) => {
        if (event.key === "ArrowDown") return current < items.length - 1 ? current + 1 : 0;
        return current > 0 ? current - 1 : items.length - 1;
      });
      return;
    }
    if (event.key === "Enter" && open && activeIndex >= 0) {
      event.preventDefault();
      const item = items[activeIndex];
      if (item) selectItem(item);
    }
  };

  return (
    <div
      ref={rootRef}
      className="hotel-map-search"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
          setActiveIndex(-1);
        }
      }}
    >
      <Input
        value={query}
        allowClear
        prefix={<Search size={18} aria-hidden="true" />}
        suffix={(
          <span className="hotel-map-search__progress" aria-label={searching ? "正在搜索" : undefined}>
            {searching ? <Spin size={14} /> : null}
          </span>
        )}
        aria-label={SEARCH_LABEL}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open ? activeOptionId : undefined}
        placeholder={SEARCH_LABEL}
        onChange={(value) => {
          onQueryChange(value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => {
          setOpen(true);
          setActiveIndex(-1);
        }}
        onKeyDown={handleKeyDown}
      />

      {open ? (
        <div
          id={listboxId}
          className="hotel-map-search__dropdown"
          role="listbox"
          aria-label="地点搜索结果"
        >
          {searching && items.length === 0 ? (
            <div className="hotel-map-search__state">
              <span>正在搜索</span>
            </div>
          ) : null}
          {areas.length > 0 ? (
            <SearchGroup
              title="房资源酒店"
              items={areas}
              offset={0}
              baseId={baseId}
              activeIndex={activeIndex}
              onSelect={selectItem}
            />
          ) : null}
          {points.length > 0 ? (
            <SearchGroup
              title="房资源酒店"
              items={points}
              offset={areas.length}
              baseId={baseId}
              activeIndex={activeIndex}
              onSelect={selectItem}
            />
          ) : null}
          {!searching && items.length === 0 ? (
            <div className="hotel-map-search__state">暂无匹配地点</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** 渲染单个语义分组及其搜索候选。 */
function SearchGroup({
  title,
  items,
  offset,
  baseId,
  activeIndex,
  onSelect,
}: {
  title: string;
  items: HotelMapSearchItem[];
  offset: number;
  baseId: string;
  activeIndex: number;
  onSelect: (item: HotelMapSearchItem) => void;
}) {
  return (
    <div className="hotel-map-search__group" role="group" aria-label={title}>
      <div className="hotel-map-search__group-title">{title}</div>
      {items.map((item, index) => {
        const itemIndex = offset + index;
        const typeLabel = "房资源";
        return (
          <div
            id={optionId(baseId, itemIndex)}
            key={`${item.type}-${item.id}`}
            className={`hotel-map-search__option${activeIndex === itemIndex ? " hotel-map-search__option--active" : ""}`}
            role="option"
            aria-selected={activeIndex === itemIndex}
            aria-label={`${item.name} ${typeLabel} ${item.regionText}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onSelect(item)}
          >
            <MapPin size={16} aria-hidden="true" />
            <div className="hotel-map-search__option-copy">
              <div className="hotel-map-search__option-line">
                <span className="hotel-map-search__name" title={item.name}>{item.name}</span>
                <Tag size="small" color="green">
                  {typeLabel}
                </Tag>
              </div>
              <span className="hotel-map-search__region" title={item.regionText}>
                {item.regionText}
              </span>
              {item.referencePrice !== undefined ? (
                <span className="hotel-map-search__price">
                  {item.currencyCode === "CNY" || !item.currencyCode ? "¥" : item.currencyCode}
                  {item.referencePrice.toFixed(0)} 参考
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 生成同一搜索下拉内稳定的候选标识。 */
function optionId(baseId: string, index: number): string {
  return `${baseId}-hotel-map-search-option-${index}`;
}
